import { motion } from "framer-motion";
import {
  Building2,
  Users,
  FileText,
  Info,
  CheckCircle2,
  Briefcase,
  Factory,
  Package,
  Truck,
  Download,
  Upload,
  MoreHorizontal,
  FileSignature,
} from "lucide-react";

const SERVICE_ICONS = {
  small_scale_smme: Building2,
  trade_mission_opportunities: Briefcase,
  general_business_advisory: FileText,
  networking_events: Users,
  other: MoreHorizontal,
};

const CATEGORY_ICONS = {
  manufacturer: Factory,
  wholesaler: Package,
  distributor: Truck,
  services: Briefcase,
  importer: Download,
  exporter: Upload,
  other: MoreHorizontal,
};

export default function Step6Preview({ formData }) {
  const formatPhoneNumber = (phone) => (phone ? phone : "N/A");

  const getServiceLabel = (value) => {
    const labels = {
      small_scale_smme: "Small Scale (SMME)",
      trade_mission_opportunities: "Trade Mission Opportunities",
      general_business_advisory: "General Business Advisory/Assistance",
      networking_events: "Networking & Events",
      other: "Other",
    };
    return labels[value] || value;
  };

  const getCategoryLabel = (value) => {
    const labels = {
      manufacturer: "Manufacturer",
      wholesaler: "Wholesaler",
      distributor: "Distributor",
      services: "Services",
      importer: "Importer",
      exporter: "Exporter",
      other: "Other",
    };
    return labels[value] || value;
  };

  const previewSections = [
    {
      id: 1,
      title: "Company Information",
      icon: Building2,
      data: [
        { label: "Company Name", value: formData.companyName || "N/A" },
        { label: "Telephone Number", value: formatPhoneNumber(formData.telephoneNumber) },
        { label: "Fax Number", value: formData.faxNumber || "N/A" },
        { label: "Website Address", value: formData.websiteAddress || "N/A" },
        { label: "VAT Registration Number", value: formData.vatRegistrationNumber || "N/A" },
        { label: "Cell Number", value: formatPhoneNumber(formData.cellNumber) },
        { label: "Business Email", value: formData.businessEmail || "N/A" },
      ],
    },
    {
      id: 2,
      title: "Contact Person Details",
      icon: Users,
      data: [
        { label: "Name", value: formData.contactPersonName || "N/A" },
        { label: "Designation", value: formData.contactPersonDesignation || "N/A" },
        { label: "Telephone Number", value: formatPhoneNumber(formData.contactPersonTelephone) },
        { label: "Cell Number", value: formatPhoneNumber(formData.contactPersonCellNumber) },
        { label: "Email Address", value: formData.contactPersonEmail || "N/A" },
      ],
    },
    {
      id: 3,
      title: "Second Contact Person Details",
      icon: Users,
      data: [
        { label: "Name", value: formData.secondContactPersonName || "N/A" },
        { label: "Designation", value: formData.secondContactPersonDesignation || "N/A" },
        { label: "Telephone Number", value: formatPhoneNumber(formData.secondContactPersonTelephone) },
        { label: "Cell Number", value: formatPhoneNumber(formData.secondContactPersonCellNumber) },
        { label: "Email Address", value: formData.secondContactPersonEmail || "N/A" },
      ],
    },
    {
      id: 4,
      title: "Company Description",
      icon: FileText,
      data: [{ label: "Description", value: formData.companyDescription || "N/A", isLongText: true }],
    },
    {
      id: 5,
      title: "Services of Interest",
      icon: Briefcase,
      data: [
        {
          label: "Selected Services",
          value: formData.selectedServices?.length ? formData.selectedServices.map(getServiceLabel).join(", ") : "N/A",
          isList: true,
          items: formData.selectedServices || [],
          otherSpec: formData.otherServiceSpecification,
        },
      ],
    },
    {
      id: 6,
      title: "Company Categories",
      icon: Info,
      data: [
        {
          label: "Selected Categories",
          value: formData.selectedCategories?.length ? formData.selectedCategories.map(getCategoryLabel).join(", ") : "N/A",
          isList: true,
          items: formData.selectedCategories || [],
          otherSpec: formData.otherCategorySpecification,
        },
      ],
    },
    {
      id: 7,
      title: "Declaration",
      icon: FileSignature,
      data: [
        { label: "Name of Applicant", value: formData.applicantName || "N/A" },
        { label: "Designation of Signatory", value: formData.signatoryDesignation || "N/A" },
        { label: "Signature of Applicant", value: formData.applicantSignature || "N/A" },
        { label: "Date", value: formData.declarationDate || "N/A" },
      ],
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3 }}
      className="p-8 bg-white rounded-xl border border-gray-200 shadow-sm"
    >
      <div className="flex gap-3 items-center mb-8">
        <div className="flex justify-center items-center w-10 h-10 text-white bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg shadow-md">
          <CheckCircle2 size={20} />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Review Your Information</h2>
          <p className="mt-1 text-sm text-gray-600">Please review all information before submitting</p>
        </div>
      </div>

      <div className="space-y-6">
        {previewSections.map((section, sectionIndex) => {
          const IconComponent = section.icon;
          return (
            <motion.div
              key={section.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: sectionIndex * 0.1 }}
              className="overflow-hidden bg-gradient-to-br from-gray-50 to-white rounded-lg border border-gray-200"
            >
              <div className="flex gap-3 items-center px-6 py-4 bg-gradient-to-r from-purple-50 to-purple-100 border-b border-purple-200">
                <div className="flex justify-center items-center w-8 h-8 text-purple-600 bg-white rounded-lg shadow-sm">
                  <IconComponent size={18} />
                </div>
                <h3 className="text-lg font-bold text-gray-900">{section.title}</h3>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  {section.data.map((item, itemIndex) => (
                    <motion.div
                      key={itemIndex}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: sectionIndex * 0.1 + itemIndex * 0.05 }}
                      className={item.isLongText || item.isList ? "md:col-span-2" : ""}
                    >
                      <div className="flex gap-2 items-start">
                        <div className="flex-shrink-0 mt-1 w-1.5 h-1.5 rounded-full bg-purple-500" />
                        <div className="flex-1 min-w-0">
                          <p className="mb-1 text-xs font-semibold tracking-wide text-gray-500 uppercase">{item.label}</p>
                          {item.isLongText ? (
                            <p className="text-sm leading-relaxed text-gray-700 whitespace-pre-wrap">{item.value}</p>
                          ) : item.isList ? (
                            <div className="space-y-2">
                              <div className="flex flex-wrap gap-2">
                                {item.items.map((listItem, idx) => {
                                  const ListIcon = section.id === 5 ? SERVICE_ICONS[listItem] : CATEGORY_ICONS[listItem];
                                  const ListIconComponent = ListIcon || MoreHorizontal;
                                  return (
                                    <motion.span
                                      key={idx}
                                      initial={{ scale: 0 }}
                                      animate={{ scale: 1 }}
                                      transition={{ delay: sectionIndex * 0.1 + idx * 0.05 }}
                                      className="inline-flex gap-1.5 items-center px-3 py-1.5 text-xs font-medium text-purple-700 bg-purple-50 border border-purple-200 rounded-lg"
                                    >
                                      <ListIconComponent size={12} />
                                      {getServiceLabel(listItem) || getCategoryLabel(listItem)}
                                    </motion.span>
                                  );
                                })}
                              </div>
                              {item.otherSpec && (
                                <div className="pt-2 mt-2 border-t border-gray-200">
                                  <p className="text-xs font-medium text-gray-500">Other Specification:</p>
                                  <p className="text-sm text-gray-700">{item.otherSpec}</p>
                                </div>
                              )}
                            </div>
                          ) : (
                            <p className="text-sm font-medium text-gray-900 break-words">{item.value}</p>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="p-6 mt-8 bg-gradient-to-r from-purple-50 to-purple-100 rounded-lg border border-purple-200"
      >
        <div className="flex gap-3 items-center">
          <CheckCircle2 size={20} className="text-purple-600" />
          <div>
            <p className="text-sm font-semibold text-gray-900">All information has been reviewed</p>
            <p className="mt-1 text-xs text-gray-600">Click &quot;Submit&quot; to complete registration on behalf of your client</p>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
